import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import {
  CheckCircle, Loader2, Send, Download, Play, AlertTriangle,
  Clock, Sparkles, RefreshCw, Copy, Check,
} from 'lucide-react';

/**
 * Rich video card used on the property page.
 *
 * Replaces the previous "Loader2 spin + raw template_id" treatment with:
 *   - Real template *name* (resolved from the catalog)
 *   - Friendly relative timestamp ("vor 2 Min.")
 *   - Live progress bar driven by the backend's `progress` field
 *   - Stage label (Wird vorbereitet → Wird gerendert → Fast fertig …)
 *   - ETA estimate based on elapsed time vs. progress
 *   - Clear action buttons in the success state
 *   - Failed/timeout states explained, with a retry handle
 *
 * Props
 *   video      – { id, render_id, template_id, status, video_url, thumbnail_url, progress, startedAt, error_message }
 *   templates . Full template catalog (so we can show the human name)
 *   fallbackThumbnail. Used when the video doesn't have a thumbnail yet
 *   onPublish  – () => void   (Veröffentlichen click)
 *   onRetry    – () => void   (Retry click on failed videos)
 */
export default function VideoCard({ video, templates, fallbackThumbnail, onPublish, onRetry }) {
  const { t } = useTranslation();
  // Defensive null-guard around `templates`. It's expected to always
  // be an array but a stale prop or in-flight render can pass undefined.
  const template = (templates || []).find(tpl => tpl.id === video.template_id);
  const templateName = template?.name || (t('videoCard.unknownTemplate') || 'Unbekannte Vorlage');
  // Status fallback. If the backend forgot to stamp a status field but
  // we have a real video_url (e.g. legacy rows, claimed-demo rows
  // before the May 2026 fix), treat the video as succeeded instead of
  // defaulting to 'processing'. Otherwise the card flips into "Wird
  // gerendert" forever for already-rendered videos. Sentinel string
  // "processing" was historically used as a video_url placeholder
  // during the render-in-flight window, so we still treat that as
  // in-progress.
  const hasRealVideoUrl =
    typeof video.video_url === 'string'
    && video.video_url.length > 0
    && video.video_url !== 'processing';
  const status = video.status || (hasRealVideoUrl ? 'succeeded' : 'processing');
  const isInProgress = ['processing', 'rendering', 'planned', 'waiting'].includes(status);
  const isReady = status === 'succeeded' && video.video_url;
  const isFailed = ['failed', 'error'].includes(status);
  const isTimeout = status === 'timeout';

  // AI-generated caption / hashtags persisted on the GeneratedVideo
  // record at render time. Replaces the old "AI Captions" tab. Captions
  // are now a property of the video, ready when the video is. Legacy
  // rows (rendered before the field existed) just don't show this row.
  //
  // Phase 4 of caption-quality work: if `per_platform_captions` is
  // populated on the video record, the "Copy caption" button opens a
  // dropdown of per-platform variants so the user grabs the
  // platform-optimized version when manually sharing. Falls back to
  // the legacy flat caption + hashtags string for older renders.
  const [copied, setCopied] = useState(null);     // tracks which platform got copied (for the checkmark)
  const [showCopyMenu, setShowCopyMenu] = useState(false);
  const caption = video.caption || '';
  const hashtags = Array.isArray(video.hashtags) ? video.hashtags : [];
  const perPlatform = video.per_platform_captions || {};
  const hasPerPlatform = Object.keys(perPlatform).length > 0;

  // Legacy "everything" string. Used when there's no per-platform
  // dict (older renders) AND as the fallback inside the dropdown.
  const fullCaption = hashtags.length
    ? `${caption}\n\n${hashtags.join(' ')}`
    : caption;

  const copyText = async (label, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(t('videoCard.captionCopied') || 'Caption kopiert');
      setShowCopyMenu(false);
      setTimeout(() => setCopied(null), 2000);
    } catch (_) {
      toast.error(t('videoCard.copyFailed') || 'Konnte nicht kopieren');
    }
  };

  // Default behavior. Copy the Instagram variant (most universal)
  // OR the legacy flat caption if no per-platform data exists.
  const copyCaption = () => {
    const universal = perPlatform.instagram || fullCaption;
    copyText('default', universal);
  };

  // Close the platform menu when clicking outside or on Escape.
  useEffect(() => {
    if (!showCopyMenu) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowCopyMenu(false); };
    const onClick = (e) => {
      // Only close if click target isn't inside a `[data-copy-menu]`.
      if (!e.target.closest?.('[data-copy-menu]')) setShowCopyMenu(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [showCopyMenu]);

  // Tick every second while a render is in progress so the elapsed/ETA
  // labels stay live without depending on parent re-renders.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isInProgress) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [isInProgress]);

  // Use whichever start-time we have. `startedAt` is set client-side when the
  // user just submitted; `created_at` is from the backend record (used when
  // the page is reloaded mid-render and we resume polling).
  const startTimeMs =
    video.startedAt
    || (video.created_at ? new Date(video.created_at).getTime() : null)
    || null;
  const elapsedSec = startTimeMs
    ? Math.max(0, Math.floor((Date.now() - startTimeMs) / 1000))
    : 0;

  // Map the coarse backend progress to a smoother displayed value:
  //  - planned/waiting → 5-17% (slow ramp)
  //  - rendering/processing → 25-90% (grows with elapsed time)
  //  - succeeded → 100%
  // This avoids the "stuck at 50%" feeling Creatomate's `progress` field gives.
  // 'processing' and 'rendering' are treated identically. They're effectively
  // the same lifecycle stage seen by the user.
  const displayProgress = (() => {
    if (isReady) return 100;
    if (isFailed || isTimeout) return Math.min(video.progress || 0, 100);
    const backend = video.progress || 0;
    if (backend >= 100) return 100;
    if (status === 'rendering' || status === 'processing') {
      const grown = 25 + Math.min(elapsedSec / 180, 1) * 65;
      return Math.max(grown, backend);
    }
    if (status === 'planned' || status === 'waiting') {
      const grown = 5 + Math.min(elapsedSec / 30, 1) * 12;
      return Math.max(grown, backend);
    }
    return backend;
  })();

  const stageLabel = (() => {
    if (isReady) return 'Bereit';
    if (isTimeout) return 'Zeitüberschreitung';
    if (isFailed) return 'Fehlgeschlagen';
    if (status === 'planned') return 'In Warteschlange';
    if (status === 'waiting') return 'Wird vorbereitet';
    if (status === 'rendering' || status === 'processing') {
      if (displayProgress > 85) return 'Fast fertig';
      if (displayProgress > 60) return 'Wird zusammengesetzt';
      return 'Wird gerendert';
    }
    return 'Wird gerendert';
  })();

  return (
    <Card
      data-testid={`generated-video-${video.id || video.render_id}`}
      // Previously used Tailwind's `animate-pulse` which fades the whole card
      // from 100% to 50% opacity every 1.5s. Visually jarring, looked broken.
      // Swapped for the `glow-pulse` keyframe in index.css that only animates
      // `box-shadow`, so the card stays solid while a soft violet halo breathes
      // around it.
      className={`glass overflow-hidden border transition-all duration-300 max-w-sm mx-auto w-full ${
        isReady
          ? 'border-[#1e7a4f]/40 hover:border-[#1e7a4f]/60 hover:-translate-y-0.5'
          : isFailed || isTimeout
          ? 'border-[#b3543f]/50'
          : 'border-[#1f4d3a]/40 glow-pulse'
      }`}
    >
      {/* Thumbnail / preview. Always 9:16 because every template we ship is
          short-form vertical. Using aspect-video on desktop used to crop the
          center strip out of the frame; `object-contain` keeps the whole
          frame visible even if a non-9:16 render ever sneaks through. */}
      <div className="relative aspect-[9/16] bg-black overflow-hidden">
        {isReady ? (
          <video
            src={video.video_url}
            poster={video.thumbnail_url || fallbackThumbnail}
            className="w-full h-full object-contain bg-black"
            controls
            controlsList="nodownload"
            preload="metadata"
            playsInline
          />
        ) : (
          <>
            <img
              src={video.thumbnail_url || fallbackThumbnail || ''}
              alt={templateName}
              className={`w-full h-full object-cover ${isInProgress ? 'opacity-30 blur-[1px]' : 'opacity-60'}`}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-4">
              {isInProgress && (
                <>
                  <div className="relative">
                    <Loader2 className="w-12 h-12 text-[#1f4d3a] animate-spin" />
                    <Sparkles className="w-5 h-5 text-[#2e6b50] absolute -bottom-1 -right-1" />
                  </div>
                  <p className="text-sm font-medium text-white drop-shadow-md">{stageLabel}</p>
                  <p className="text-xs text-[#4a463b] drop-shadow">{Math.round(displayProgress)}% · {formatElapsed(elapsedSec)}</p>
                </>
              )}
              {isFailed && (
                <>
                  <AlertTriangle className="w-10 h-10 text-[#b3543f]" />
                  <p className="text-sm font-medium text-white drop-shadow-md">{stageLabel}</p>
                  {video.error_message && (
                    <p className="text-xs text-[#8c3a29] max-w-xs">{video.error_message}</p>
                  )}
                </>
              )}
              {isTimeout && (
                <>
                  <Clock className="w-10 h-10 text-[#9a6d1f]" />
                  <p className="text-sm font-medium text-white drop-shadow-md">Zeitüberschreitung</p>
                  <p className="text-xs text-[#9a6d1f] max-w-xs">Render dauerte länger als erwartet. Bitte erneut versuchen.</p>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Live progress bar (only while rendering) */}
      {isInProgress && (
        <div className="h-1 w-full bg-[#fffdf7] overflow-hidden">
          <div
            className="h-full bg-[#1f4d3a] transition-[width] duration-700 ease-out"
            style={{
              width: `${displayProgress}%`,
              backgroundSize: '200% 100%',
              animation: 'shimmer 2s linear infinite',
            }}
          />
        </div>
      )}

      {/* Body */}
      <CardContent className="p-4 sm:p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-[#23211a] truncate" title={templateName}>
              {templateName}
            </p>
            <p className="text-xs text-[#8a8474] mt-0.5">
              {formatRelativeTime(
                video.startedAt || (video.created_at ? new Date(video.created_at).getTime() : null)
              )}
            </p>
          </div>
          <StatusPill status={status} />
        </div>

        {/* Caption preview. Only shown for ready videos that actually
            have a caption persisted. Replaces the old separate "AI
            Captions" tab so the user sees the post copy alongside the
            video itself. line-clamp-3 keeps the card scannable; full
            caption is in the clipboard on copy. */}
        {isReady && caption && (
          <div className="rounded-lg bg-white border border-[#e3dccb] p-3 space-y-2">
            <p className="text-xs text-[#23211a] leading-relaxed line-clamp-3 whitespace-pre-line">
              {caption}
            </p>
            {hashtags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {hashtags.slice(0, 8).map((tag, i) => (
                  <span
                    key={i}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-[#f5eede] text-[#8f6f3a] border border-[#c7a869]/40"
                  >
                    {tag}
                  </span>
                ))}
                {hashtags.length > 8 && (
                  <span className="text-[10px] text-[#8a8474]">+{hashtags.length - 8}</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {isReady ? (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              size="sm"
              className="flex-1 bg-[#1f4d3a] hover:bg-[#163a2c] text-white"
              onClick={onPublish}
            >
              <Send className="w-4 h-4 mr-1.5" />
              Veröffentlichen
            </Button>
            {caption && (
              <div className="relative" data-copy-menu>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#d8d0bd] text-[#23211a] hover:bg-[#efe9db]"
                  onClick={() => {
                    // No per-platform data → just copy and bail (no menu).
                    if (!hasPerPlatform) {
                      copyCaption();
                      return;
                    }
                    // Has per-platform → toggle the menu.
                    setShowCopyMenu((v) => !v);
                  }}
                  aria-label={t('videoCard.copyAria') || 'Caption kopieren'}
                  title={hasPerPlatform
                    ? (t('videoCard.copyMenuHint') || 'Pro Plattform passende Caption kopieren')
                    : (t('videoCard.copyAria') || 'Caption kopieren')}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                {/* Per-platform dropdown. Only renders when we have
                    a per-platform map. Click outside / Esc closes
                    via the useEffect hook above. */}
                {showCopyMenu && hasPerPlatform && (
                  <div
                    data-copy-menu
                    className="absolute right-0 top-full mt-1 z-20 min-w-[200px] bg-[#fffdf7] border border-[#d8d0bd] rounded-lg shadow-xl py-1 text-sm"
                  >
                    <p className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-[#8a8474]">
                      {t('videoCard.copyForPlatform') || 'Kopieren für…'}
                    </p>
                    {Object.entries(perPlatform).map(([platform, text]) => (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => copyText(platform, text)}
                        className="w-full text-left px-3 py-1.5 text-[#23211a] hover:bg-[#efe9db] flex items-center justify-between gap-2"
                      >
                        <span className="capitalize">{platform}</span>
                        {copied === platform && <Check className="w-3.5 h-3.5 text-[#1e7a4f]" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              className="border-[#d8d0bd] text-[#23211a] hover:bg-[#efe9db]"
              onClick={() => window.open(video.video_url, '_blank')}
              aria-label="Video herunterladen"
            >
              <Download className="w-4 h-4" />
            </Button>
          </div>
        ) : isInProgress ? (
          <div className="text-xs text-[#6f6a5c] flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Schritt: <span className="text-[#23211a] font-medium">{stageLabel}</span></span>
          </div>
        ) : (isFailed || isTimeout) && onRetry ? (
          <Button
            size="sm"
            variant="outline"
            className="w-full border-[#b3543f]/50 text-[#8c3a29] hover:bg-[#f7e8e3]"
            onClick={onRetry}
          >
            <RefreshCw className="w-4 h-4 mr-1.5" />
            Erneut versuchen
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}


function StatusPill({ status }) {
  const map = {
    succeeded: { text: 'Bereit', className: 'bg-[#e4f0e8] text-[#1e7a4f] border-[#1e7a4f]/40', icon: CheckCircle },
    rendering: { text: 'Rendert', className: 'bg-[#e9efe7] text-[#1f4d3a] border-[#1f4d3a]/30', icon: Loader2, spin: true },
    planned: { text: 'In Warteschlange', className: 'bg-[#d8d0bd]/40 text-[#4a463b] border-[#d8d0bd]', icon: Clock },
    waiting: { text: 'Wartet', className: 'bg-[#d8d0bd]/40 text-[#4a463b] border-[#d8d0bd]', icon: Clock },
    processing: { text: 'Wird erstellt', className: 'bg-[#e9efe7] text-[#1f4d3a] border-[#1f4d3a]/30', icon: Loader2, spin: true },
    failed: { text: 'Fehler', className: 'bg-[#b3543f]/15 text-[#a34430] border-[#b3543f]/40', icon: AlertTriangle },
    error: { text: 'Fehler', className: 'bg-[#b3543f]/15 text-[#a34430] border-[#b3543f]/40', icon: AlertTriangle },
    timeout: { text: 'Timeout', className: 'bg-[#c7a869]/15 text-[#9a6d1f] border-[#c7a869]', icon: Clock },
  };
  const entry = map[status] || map.processing;
  const Icon = entry.icon;
  return (
    <span className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full border ${entry.className}`}>
      <Icon className={`w-3 h-3 ${entry.spin ? 'animate-spin' : ''}`} />
      {entry.text}
    </span>
  );
}


function formatElapsed(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}


function formatRelativeTime(timestampMs) {
  if (!timestampMs) return '';
  const diffSec = Math.floor((Date.now() - timestampMs) / 1000);
  if (diffSec < 5) return 'Gerade eben';
  if (diffSec < 60) return `vor ${diffSec} Sek.`;
  if (diffSec < 3600) return `vor ${Math.floor(diffSec / 60)} Min.`;
  if (diffSec < 86400) return `vor ${Math.floor(diffSec / 3600)} Std.`;
  return `vor ${Math.floor(diffSec / 86400)} Tagen`;
}
